import { inject, Injectable } from '@angular/core';
import { PersistDataService } from '../app-state/persist-data.service';
import { ExtractedLocationData } from '../../models/interfaces';
import { ById } from '../../models/extracted-values';
import { OcrMultiSelection } from '../../models/ocr-selection-model';
import { WordRect } from '@ps-gui/claims-terminal-shared';

type LocationsByFieldId = Record<string, ExtractedLocationData>;

@Injectable({
  providedIn: 'root',
})
export class ExtractedLocationStoreService {
  private readonly persistDataService = inject(PersistDataService);

  private extractedLocationsByDocumentId: ById<LocationsByFieldId> =
    this.persistDataService.extractedLocationsByDocumentId ?? {};

  setExtractedFieldLocations(documentId: string, locations: LocationsByFieldId) {
    this.extractedLocationsByDocumentId[documentId] = locations;
    this.storeLocations();
  }

  getExtractedLocation(documentId: string, fieldId: string): ExtractedLocationData | undefined {
    return this.extractedLocationsByDocumentId[documentId]?.[fieldId];
  }

  getExtractedLocations(documentId: string): LocationsByFieldId {
    return this.extractedLocationsByDocumentId[documentId] ?? {};
  }

  appendExtractedLocation(
    documentId: string,
    pageId: string,
    fieldId: string,
    wordRects: WordRect[],
  ) {
    const previousSelection = this.getExtractedLocation(documentId, fieldId)?.selection ?? [];

    this.setExtractedLocation(documentId, pageId, fieldId, previousSelection.concat([wordRects]));
  }

  replaceExtractedLocation(
    documentId: string,
    pageId: string,
    fieldId: string,
    wordRects: WordRect[],
  ) {
    this.setExtractedLocation(documentId, pageId, fieldId, [wordRects]);
  }

  private setExtractedLocation(
    documentId: string,
    pageId: string,
    fieldId: string,
    selection: OcrMultiSelection,
  ) {
    if (!(documentId in this.extractedLocationsByDocumentId)) {
      this.extractedLocationsByDocumentId[documentId] = {};
    }

    this.extractedLocationsByDocumentId[documentId]![fieldId] = { pageId, selection };

    this.storeLocations();
  }

  deleteExtractedLocation(documentId: string, fieldId: string) {
    delete this.extractedLocationsByDocumentId[documentId]?.[fieldId];
    this.storeLocations();
  }

  storeLocations() {
    this.persistDataService.extractedLocationsByDocumentId = this.extractedLocationsByDocumentId;
  }
}
